import { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, FileDown, CheckCircle2, AlertTriangle, Loader2, ImagePlus, X } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";

const COLS = ["name", "category", "brand", "description", "mrp", "price", "tax_pct", "stock", "return_type", "return_window_days", "delivery_speed", "delivery_min_days", "delivery_max_days", "delivery_lead_days", "shipping_charge", "image_urls", "variant_options", "image_files"];

const splitPipe = (v) => String(v || "").split("|").map((s) => s.trim()).filter(Boolean);

export default function SellerBulkUpload({ onDone }) {
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [photoMap, setPhotoMap] = useState({}); // lower(filename) -> {url, name}

  const getTemplate = async (fmt) => {
    try { await downloadFile(`/marketplace/seller/bulk/template?format=${fmt}`, `bulk_products_template.${fmt}`); }
    catch { toast.error("Could not download the template. Please try again."); }
  };

  // Merge device-photo filenames (image_files) into image_urls, non-destructively.
  const mergePhotos = (data) => {
    const files = splitPipe(data.image_files);
    if (!files.length) return data;
    const resolved = files.map((f) => photoMap[f.toLowerCase()]?.url).filter(Boolean);
    if (!resolved.length) return data;
    const merged = Array.from(new Set([...splitPipe(data.image_urls), ...resolved]));
    return { ...data, image_urls: merged.join(" | ") };
  };

  const runValidate = async (dataRows) => {
    const r = await api.post("/marketplace/seller/bulk/validate", { rows: dataRows.map(mergePhotos) });
    setRows(r.data.rows); setSummary(r.data.summary);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/marketplace/seller/bulk/parse", fd);
      // If photos are already uploaded, resolve filenames straight away.
      if (Object.keys(photoMap).length && r.data.rows.some((x) => splitPipe(x.data.image_files).length)) {
        await runValidate(r.data.rows.map((x) => x.data));
      } else {
        setRows(r.data.rows); setSummary(r.data.summary);
      }
      setConfirm(false);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotoBusy(true);
    const next = { ...photoMap };
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/marketplace/product/image/upload", fd);
        next[f.name.toLowerCase()] = { url: r.data.url, name: f.name };
        ok += 1;
      } catch { fail += 1; }
    }
    setPhotoMap(next);
    setPhotoBusy(false);
    if (photoRef.current) photoRef.current.value = "";
    if (ok) toast.success(`${ok} photo(s) uploaded${fail ? `, ${fail} failed` : ""}`);
    else if (fail) toast.error("Photos could not be uploaded. Use JPG/PNG/WebP under 10 MB.");
    // Re-resolve against the current rows so the preview reflects matches.
    if (rows && ok) {
      const merged = { ...photoMap, ...next };
      const resolveWith = (data) => {
        const fnames = splitPipe(data.image_files);
        if (!fnames.length) return data;
        const res = fnames.map((x) => merged[x.toLowerCase()]?.url).filter(Boolean);
        if (!res.length) return data;
        return { ...data, image_urls: Array.from(new Set([...splitPipe(data.image_urls), ...res])).join(" | ") };
      };
      try {
        const r = await api.post("/marketplace/seller/bulk/validate", { rows: rows.map((x) => resolveWith(x.data)) });
        setRows(r.data.rows); setSummary(r.data.summary);
      } catch { /* keep existing preview */ }
    }
  };

  const removePhoto = (key) => setPhotoMap((m) => { const n = { ...m }; delete n[key]; return n; });

  const editCell = (idx, key, val) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, data: { ...r.data, [key]: val } } : r));

  const recheck = async () => {
    setBusy(true);
    try { await runValidate(rows.map((r) => r.data)); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };

  const submit = async () => {
    if (!confirm) { toast.error("Please tick the confirmation box."); return; }
    const ready = rows.filter((r) => r.ready).map((r) => mergePhotos(r.data));
    if (!ready.length) { toast.error("No ready rows to submit. Fix the flagged rows first."); return; }
    setBusy(true);
    try {
      const r = await api.post("/marketplace/seller/bulk/submit", { rows: ready, confirm: true });
      setResult(r.data);
      toast.success(`${r.data.created} product(s) submitted for verification!`);
      if (r.data.created) { setRows(null); setSummary(null); setConfirm(false); onDone?.(); }
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };

  const photoList = Object.entries(photoMap);
  // Count device-photo references across rows that we couldn't match to an uploaded file.
  const unmatched = rows ? Array.from(new Set(
    rows.flatMap((r) => splitPipe(r.data.image_files)).filter((f) => !photoMap[f.toLowerCase()])
  )) : [];

  return (
    <div data-testid="seller-bulk-upload" className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="flex items-center gap-2 text-base font-bold text-slate-800"><UploadCloud className="h-5 w-5 text-blue-500" /> Bulk product upload</h3>
        <p className="mt-1 text-sm text-slate-500">Add many products at once with a spreadsheet. Each row is one product. Every product goes through the <b>same verification</b> — nothing goes live until approved. Use the <b>variant_options</b> column to create size/colour options, and upload device photos below to attach them by filename.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => getTemplate("csv")} data-testid="bulk-template-csv" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"><FileDown className="h-4 w-4" /> Download CSV template</button>
          <button onClick={() => getTemplate("xlsx")} data-testid="bulk-template-xlsx" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"><FileSpreadsheet className="h-4 w-4" /> Download Excel template</button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white" data-testid="bulk-upload-label">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload filled file
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
          </label>
        </div>
      </div>

      {/* Device photos zone */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="bulk-photos">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800"><ImagePlus className="h-4 w-4 text-purple-500" /> Attach device photos (optional)</h4>
            <p className="mt-0.5 text-xs text-slate-500">Upload product photos here, then reference their filenames in the sheet's <b>image_files</b> column (e.g. <code>shirt1.jpg | shirt2.jpg</code>).</p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-600 hover:bg-purple-100" data-testid="bulk-photos-label">
            {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Upload photos
            <input ref={photoRef} type="file" accept="image/*" multiple onChange={onPhotos} className="hidden" data-testid="bulk-photos-input" />
          </label>
        </div>
        {photoList.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="bulk-photos-list">
            {photoList.map(([key, v]) => (
              <span key={key} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 py-1 pl-1 pr-2 text-xs" data-testid={`bulk-photo-${key}`}>
                <img src={v.url} alt={v.name} className="h-8 w-8 rounded object-cover" />
                <span className="max-w-[140px] truncate text-slate-600">{v.name}</span>
                <button onClick={() => removePhoto(key)} data-testid={`bulk-photo-remove-${key}`} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {result && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="bulk-result">
          <p className="font-semibold text-emerald-800">{result.created} product(s) submitted for verification.</p>
          {result.failed?.length > 0 && (
            <div className="mt-2 text-amber-700">
              <p className="font-semibold">{result.failed.length} row(s) were skipped:</p>
              {result.failed.map((f, i) => <p key={i}>• Row {f.row}{f.name ? ` (${f.name})` : ""}: {f.error}</p>)}
            </div>
          )}
        </div>
      )}

      {rows && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="bulk-preview">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-700" data-testid="bulk-summary">
              <span className="text-emerald-600">{summary.ready} ready</span>{summary.errors > 0 && <>, <span className="text-amber-600">{summary.errors} need fixing</span></>} · {summary.total} total
            </p>
            <button onClick={recheck} disabled={busy} data-testid="bulk-recheck" className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 disabled:opacity-50">Re-check rows</button>
          </div>
          {unmatched.length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700" data-testid="bulk-photos-unmatched">
              {unmatched.length} photo filename(s) in the sheet aren't uploaded yet: {unmatched.slice(0, 6).join(", ")}{unmatched.length > 6 ? "…" : ""}. Upload them above, then re-check.
            </div>
          )}
          <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                <tr><th className="p-2">#</th><th className="p-2">Status</th>{COLS.map((c) => <th key={c} className="whitespace-nowrap p-2">{c}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${r.ready ? "" : "bg-amber-50/50"}`} data-testid={`bulk-row-${idx}`}>
                    <td className="p-2 text-slate-400">{r.row}</td>
                    <td className="p-2">
                      {r.ready
                        ? <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`bulk-row-ok-${idx}`}><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>
                        : <span className="group relative inline-flex items-center gap-1 text-amber-600" data-testid={`bulk-row-err-${idx}`} title={r.errors.join("; ")}><AlertTriangle className="h-3.5 w-3.5" /> {r.errors.length} issue(s)</span>}
                    </td>
                    {COLS.map((c) => (
                      <td key={c} className="p-1">
                        <input value={r.data[c] ?? ""} onChange={(e) => editCell(idx, c, e.target.value)} data-testid={`bulk-cell-${idx}-${c}`}
                          className="w-28 rounded border border-slate-200 px-1.5 py-1 focus:border-blue-400 focus:outline-none" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.some((r) => !r.ready) && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="bulk-errors-list">
              {rows.filter((r) => !r.ready).map((r) => <p key={r.row}>• Row {r.row}: {r.errors.join("; ")}</p>)}
            </div>
          )}
          <label className="mt-4 flex items-start gap-2 text-xs text-slate-600" data-testid="bulk-confirm-label">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="bulk-confirm" className="mt-0.5" />
            I confirm the details for these products are accurate and comply with the marketplace policies, to the best of my knowledge.
          </label>
          <button onClick={submit} disabled={busy || !confirm || summary.ready === 0} data-testid="bulk-submit" className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit {summary.ready} ready product(s) for verification
          </button>
        </div>
      )}
    </div>
  );
}
